import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IProminentCaseSummary } from '@bvi/api-interfaces/response/case';
import { DataPopover, Div } from '@bvi/common-components';
import { formatDate } from '@bvi/date-utils';

import { formatCurrency, formatNumber } from '../../lib/formatter';
import { i18nInstance } from '../../locales';

import { styles } from './styles';

interface IResultsProminentCasesProperties {
  prominentCases?: Array<IProminentCaseSummary>;
  isLoading?: boolean;
}

export const ResultsProminentCases: React.FC<IResultsProminentCasesProperties> =
  memo(({ prominentCases = [], isLoading = false }) => {
    const { t } = useTranslation('', {
      i18n: i18nInstance,
      keyPrefix: 'results.prominentCases',
    });

    if (!isLoading && prominentCases.length === 0) {
      return null;
    }

    return (
      <Div sx={styles.wrapper}>
        <DataPopover
          title={
            isLoading ? <Skeleton sx={styles.titleSkeleton} /> : t('title')
          }
        >
          {isLoading ? (
            <Skeleton sx={styles.tableSkeleton} />
          ) : (
            <Table size="small" sx={styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.headerCell}>
                    {t('columns.name')}
                  </TableCell>
                  <TableCell sx={styles.headerCell}>
                    {t('columns.organization')}
                  </TableCell>
                  <TableCell sx={styles.headerCell}>
                    {t('columns.resolutionDate')}
                  </TableCell>
                  <TableCell sx={styles.headerCell} align="right">
                    {t('columns.amount')}
                  </TableCell>
                  <TableCell sx={styles.headerCell} align="right">
                    {t('columns.claimants')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prominentCases.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={styles.bodyCell}>
                      {row.perpetratorName ?? '—'}
                    </TableCell>
                    <TableCell sx={styles.bodyCell}>
                      {row.organizationName ?? '—'}
                    </TableCell>
                    <TableCell sx={styles.bodyCell}>
                      {row.resolutionDate
                        ? formatDate(row.resolutionDate)
                        : '—'}
                    </TableCell>
                    <TableCell sx={styles.bodyCell} align="right">
                      {row.totalAmount === null || row.totalAmount === undefined
                        ? '—'
                        : formatCurrency(row.totalAmount)}
                    </TableCell>
                    <TableCell sx={styles.bodyCell} align="right">
                      {row.claimantsCount === null ||
                      row.claimantsCount === undefined
                        ? '—'
                        : formatNumber(row.claimantsCount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DataPopover>
      </Div>
    );
  });
